/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import {Text, View} from 'react-native';
import noop from 'lodash/noop';

import Button from '/component/base/Button';
import FormField from '/component/base/FormField';
import Icon from '/component/base/Icon';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {AccessibilityWrapper} from '/component/layout/AccessibilityWrapper';
import {SearchAndFilter} from '/constant/TestIDs';
import testProperties from '/util/testing/testProperties';

import {jungleGreen} from '../../../../theme/color/on.color';

import {useSearchHeader} from './SearchHeader.hook';
import {styles} from './SearchHeader.style';

type SearchHeaderProps = {
  getTheme: (key: string) => string;
  navigateBack: () => void;
  fetchSearchResults: () => void;
  searchInput: string;
  hasSearchResults: boolean;
  updateSearchInput: (input: string) => void;
  recordsParams: Record<string, any>;
};

export const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchInput = '',
  getTheme = noop,
  updateSearchInput = noop,
  navigateBack = noop,
  hasSearchResults = false,
  fetchSearchResults = noop,
  recordsParams = {},
}) => {
  const {ts, onChangeText, onClear} = useSearchHeader({
    searchInput,
    updateSearchInput,
    hasSearchResults,
    fetchSearchResults,
    recordsParams,
  });

  const focusedFormFieldStyle = {
    ...styles.focusedFormField,
    borderColor: jungleGreen,
  };

  const getSearchAccessibilityLabel = (value: string) => {
    if (!value) return undefined;

    const isNumericOnly = /^[0-9]+$/.test(value.trim());

    return isNumericOnly ? 'Search by ID' : 'Search by name';
  };

  return (
    <View style={styles.container}>
      <AccessibilityWrapper style={styles.row}>
        <View style={styles.formFieldWrapper}>
          <FormField
            value={searchInput}
            style={{
              base: styles.formField,
              focused: focusedFormFieldStyle,
              textInput: styles.textInput,
            }}
            placeholder={ts('searchPlaceholder', 'Search by name or ID')}
            onChangeText={onChangeText}
            accessibilityLabel={getSearchAccessibilityLabel(searchInput)}
            returnKeyType="search"
            accessible={true}
            onClear={onClear}
            testID={SearchAndFilter.searchInput}
          />

          {searchInput ? (
            <TouchableOpacity
              style={styles.btnClear}
              accessible={true}
              accessibilityLabel="Clear Search"
              accessibilityRole="button"
              onPress={onClear}
              {...testProperties(null, false)}>
              <Icon name="close" style={styles.clearIcon} />
            </TouchableOpacity>
          ) : null}
        </View>

        <Button
          accessible={true}
          accessibilityLabel="Cancel"
          accessibilityRole="button"
          onPress={navigateBack}
          type="textLink"
          style={{
            text: {fontWeight: 'bold'},
            base: {marginLeft: 10, paddingVertical: 12},
          }}
          testID={SearchAndFilter.searchCancelButton}>
          {ts('cancel', 'Cancel')}
        </Button>
      </AccessibilityWrapper>

      {searchInput.length > 0 && (
        <Text
          accessible={true}
          accessibilityLabel="Search by name or ID label"
          style={styles.label}>
          Search by name or ID
        </Text>
      )}
    </View>
  );
};
